import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FilterListIcon from "@material-ui/icons/FilterList";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AddCommentIcon from "@material-ui/icons/AddComment";
import AddIcon from "@material-ui/icons/Add";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useHistory } from "react-router-dom";
import OsInteractionDialog from "../../components/OsInteractionDialog";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useOsCatalogs from "../../hooks/useOsCatalogs";
import { AuthContext } from "../../context/Auth/AuthContext";
import { formatOsPrevisao } from "../../utils/osDates";

const initialFilters = {
  id: "",
  q: "",
  pesquisar_interacoes: false,
  grupo_id: "",
  objeto_id: "",
  tecnico_id: "",
  prioridade_id: "",
  tipo_id: "",
  canal_id: "",
  cliente_id: "",
  tecnico_inclusao_id: "",
  objeto_padrao_id: "",
  status: "abertas",
  data_inclusao_inicio: "",
  data_inclusao_fim: "",
  data_fechamento_inicio: "",
  data_fechamento_fim: "",
  previsao_inicio: "",
  previsao_fim: ""
};

const createInitialFilters = tecnicoId => ({
  ...initialFilters,
  tecnico_id: tecnicoId || ""
});

const emptyCatalogs = {
  grupos: [],
  status: [],
  prioridades: [],
  tipos: [],
  canais: [],
  tecnicos: [],
  objetos: [],
  objetosConclusao: [],
  objetosPadrao: [],
  clientes: []
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  filtersPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  tableContainer: {
    flex: 1,
    "& .MuiTableCell-root": {
      padding: theme.spacing(1, 1.25)
    }
  },
  tableHead: {
    backgroundColor: theme.palette.background.default
  },
  orderColumn: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    whiteSpace: "nowrap",
    boxShadow: `1px 0 0 ${theme.palette.divider}`
  },
  orderColumnHead: {
    position: "sticky",
    left: 0,
    zIndex: 4,
    backgroundColor: theme.palette.background.default,
    whiteSpace: "nowrap",
    boxShadow: `1px 0 0 ${theme.palette.divider}`
  },
  orderLink: {
    display: "inline-flex",
    alignItems: "center",
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  quickInteraction: {
    marginLeft: theme.spacing(0.5)
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover $orderColumn, &:focus $orderColumn": {
      backgroundColor: theme.palette.action.hover
    },
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2
    }
  },
  nowrap: {
    whiteSpace: "nowrap"
  },
  descriptionCell: {
    minWidth: 210,
    maxWidth: 320
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  filterActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  },
}));

const formatDate = value => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(date);
};

const payloadItems = response => response?.data?.data || [];

const OrdensServico = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [catalogs, setCatalogs] = useState(emptyCatalogs);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientSearch, setClientSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [interactionOrder, setInteractionOrder] = useState(null);
  const {
    data: sharedCatalogs,
    isLoading: sharedCatalogsLoading,
    error: sharedCatalogsError
  } = useOsCatalogs();
  const catalogsLoading = sharedCatalogsLoading || clientsLoading;
  const userId = user?.id;
  const userTechnicianId = user?.IDTecnicoOS;

  useEffect(() => {
    if (!userId) return;

    const defaults = createInitialFilters(userTechnicianId);
    setFilters(defaults);
    setAppliedFilters(defaults);
    setPage(0);
    setFiltersInitialized(true);
  }, [userId, userTechnicianId]);

  useEffect(() => {
    if (!sharedCatalogs) return;
    setCatalogs(previous => ({ ...previous, ...sharedCatalogs }));
  }, [sharedCatalogs]);

  useEffect(() => {
    if (sharedCatalogsError) toastError(sharedCatalogsError);
  }, [sharedCatalogsError]);

  useEffect(() => {
    let active = true;

    const loadClients = async () => {
      setClientsLoading(true);
      try {
        const response = await api.get("/os/clientes", {
          params: { per_page: 200 }
        });
        if (!active) return;
        setCatalogs(previous => ({
          ...previous,
          clientes: payloadItems(response)
        }));
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setClientsLoading(false);
      }
    };

    loadClients();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!clientSearch.trim()) return undefined;

    let active = true;
    const timeout = setTimeout(async () => {
      try {
        const response = await api.get("/os/clientes", {
          params: { q: clientSearch, per_page: 50 }
        });
        if (!active) return;
        setCatalogs(previous => ({
          ...previous,
          clientes: payloadItems(response)
        }));
      } catch (err) {
        if (active) toastError(err);
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [clientSearch]);

  const buildParams = useCallback(() => {
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
      ordenar: "id_desc"
    };

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value !== "" && value !== false && key !== "status") {
        params[key] = value;
      }
    });

    if (appliedFilters.pesquisar_interacoes) {
      params.pesquisar_interacoes = true;
    }
    if (appliedFilters.status === "abertas") {
      params.abertas = true;
    } else if (appliedFilters.status) {
      params.status_id = appliedFilters.status;
    }

    return params;
  }, [appliedFilters, page, rowsPerPage]);

  useEffect(() => {
    let active = true;

    if (!filtersInitialized) return undefined;

    const loadOrders = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/os/ordens-servico", {
          params: buildParams()
        });
        if (!active) return;
        setOrders(data.data || []);
        setTotal(data.meta?.total || 0);
      } catch (err) {
        if (active) toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadOrders();
    return () => {
      active = false;
    };
  }, [buildParams, filtersInitialized, reloadKey]);

  const changeFilter = event => {
    const { name, value, checked, type } = event.target;
    setFilters(previous => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
      ...(name === "objeto_id" ? { objeto_padrao_id: "" } : {})
    }));
  };

  const applyFilters = () => {
    setPage(0);
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    const defaults = createInitialFilters(user?.IDTecnicoOS);
    setFilters(defaults);
    setAppliedFilters(defaults);
    setPage(0);
  };

  const openDetails = order => {
    history.push(`/ordens-servico/${order.id}`);
  };

  const interactionSaved = () => {
    setInteractionOrder(null);
    setReloadKey(key => key + 1);
  };

  const selectField = (name, label, options, firstLabel = "Todos") => (
    <FormControl variant="outlined" size="small" fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        name={name}
        value={filters[name]}
        onChange={changeFilter}
        label={label}
      >
        <MenuItem value="">{firstLabel}</MenuItem>
        {options.map(option => (
          <MenuItem key={option.id} value={option.id}>
            {option.nome || option.razao || option.fantasia}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const filteredDefaultObjects = filters.objeto_id
    ? catalogs.objetosPadrao.filter(
        item => String(item.objeto_id) === String(filters.objeto_id)
      )
    : catalogs.objetosPadrao;

  return (
    <MainContainer maxWidth={false}>
      <MainHeader>
        <Title>Ordens de Serviço</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => history.push("/nova-ordem-servico")}
          >
            Nova OS
          </Button>
          <TextField
            name="q"
            value={filters.q}
            onChange={changeFilter}
            onKeyDown={event => {
              if (event.key === "Enter") applyFilters();
            }}
            placeholder="Descrição, cliente ou documento"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
          <Tooltip title="Filtros">
            <IconButton
              color={filtersOpen ? "primary" : "default"}
              onClick={() => setFiltersOpen(open => !open)}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Atualizar">
            <IconButton onClick={() => setReloadKey(key => key + 1)}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <OsInteractionDialog
        open={Boolean(interactionOrder)}
        onClose={() => setInteractionOrder(null)}
        onSaved={interactionSaved}
        order={interactionOrder}
        objectOptions={catalogs.objetosConclusao}
        canFinalize={!interactionOrder?.data_fechada}
      />

      {filtersOpen && (
        <Paper className={classes.filtersPaper} variant="outlined">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                name="id"
                label="Número da OS"
                value={filters.id}
                onChange={changeFilter}
                variant="outlined"
                size="small"
                type="number"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={5} md={3}>
              {selectField("grupo_id", "Grupo", catalogs.grupos)}
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={filters.status}
                  onChange={changeFilter}
                  label="Status"
                >
                  <MenuItem value="abertas">Todas abertas</MenuItem>
                  <MenuItem value="">Todos</MenuItem>
                  {catalogs.status.map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={catalogs.clientes}
                value={
                  catalogs.clientes.find(
                    item => String(item.id) === String(filters.cliente_id)
                  ) || null
                }
                getOptionLabel={option =>
                  option.razao || option.fantasia || `Cliente ${option.id}`
                }
                getOptionSelected={(option, value) => option.id === value.id}
                onInputChange={(_, value) => setClientSearch(value)}
                onChange={(_, value) =>
                  setFilters(previous => ({
                    ...previous,
                    cliente_id: value?.id || ""
                  }))
                }
                loading={catalogsLoading}
                noOptionsText="Nenhum cliente encontrado"
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Cliente"
                    variant="outlined"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {catalogsLoading ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              {selectField("objeto_id", "Objeto", catalogs.objetos)}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {selectField(
                "objeto_padrao_id",
                "Objeto padrão",
                filteredDefaultObjects
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {selectField("tecnico_id", "Técnico", catalogs.tecnicos)}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {selectField(
                "tecnico_inclusao_id",
                "Técnico (inclusão)",
                catalogs.tecnicos
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              {selectField(
                "prioridade_id",
                "Prioridade",
                catalogs.prioridades
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {selectField("tipo_id", "Tipo", catalogs.tipos)}
            </Grid>
            <Grid item xs={12} sm={4}>
              {selectField("canal_id", "Canal", catalogs.canais)}
            </Grid>

            {[
              ["data_inclusao_inicio", "Inclusão de"],
              ["data_inclusao_fim", "Inclusão até"],
              ["data_fechamento_inicio", "Fechamento de"],
              ["data_fechamento_fim", "Fechamento até"],
              ["previsao_inicio", "Previsão de"],
              ["previsao_fim", "Previsão até"]
            ].map(([name, label]) => (
              <Grid item xs={12} sm={4} md={2} key={name}>
                <TextField
                  name={name}
                  label={label}
                  value={filters[name]}
                  onChange={changeFilter}
                  type="date"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            ))}
          </Grid>

          <Box className={classes.filterActions}>
            <FormControlLabel
              control={
                <Checkbox
                  name="pesquisar_interacoes"
                  checked={filters.pesquisar_interacoes}
                  onChange={changeFilter}
                  color="primary"
                />
              }
              label="Pesquisar nas interações"
            />
            {catalogsLoading && <CircularProgress size={24} />}
            <Button onClick={clearFilters}>Limpar</Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FilterListIcon />}
              onClick={applyFilters}
            >
              Filtrar
            </Button>
          </Box>
        </Paper>
      )}

      <Paper className={classes.mainPaper} variant="outlined">
        <TableContainer className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.orderColumnHead}>OS</TableCell>
                <TableCell className={classes.tableHead}>Grupo</TableCell>
                <TableCell className={classes.tableHead}>Inclusão</TableCell>
                <TableCell className={classes.tableHead}>Previsão início</TableCell>
                <TableCell className={classes.tableHead}>Previsão término</TableCell>
                <TableCell className={classes.tableHead}>Técnico</TableCell>
                <TableCell className={classes.tableHead}>Descrição</TableCell>
                <TableCell className={classes.tableHead}>Cliente</TableCell>
                <TableCell className={classes.tableHead}>Objeto</TableCell>
                <TableCell className={classes.tableHead}>Status</TableCell>
                <TableCell className={classes.tableHead}>Tipo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                orders.map(order => (
                  <TableRow
                    hover
                    key={order.id}
                    className={classes.clickableRow}
                    tabIndex={0}
                    aria-label={`Abrir ordem de serviço ${order.id}`}
                    onClick={() => openDetails(order)}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openDetails(order);
                      }
                    }}
                  >
                    <TableCell className={classes.orderColumn}>
                      <span className={classes.orderLink}>
                        <ChevronRightIcon fontSize="small" />
                        {order.id}
                      </span>
                      <Tooltip title={`Interagir na OS #${order.id}`}>
                        <IconButton
                          className={classes.quickInteraction}
                          size="small"
                          color="primary"
                          aria-label={`Interagir na ordem de serviço ${order.id}`}
                          onClick={event => {
                            event.stopPropagation();
                            setInteractionOrder(order);
                          }}
                          onKeyDown={event => event.stopPropagation()}
                        >
                          <AddCommentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{order.grupo?.nome || "-"}</TableCell>
                    <TableCell className={classes.nowrap}>
                      {formatDate(order.data_hora)}
                    </TableCell>
                    <TableCell className={classes.nowrap}>
                      {formatOsPrevisao(order.data_previsao_inicio)}
                    </TableCell>
                    <TableCell className={classes.nowrap}>
                      {formatOsPrevisao(order.data_previsao_termino)}
                    </TableCell>
                    <TableCell>{order.tecnico?.nome || "-"}</TableCell>
                    <TableCell className={classes.descriptionCell}>
                      {order.descricao_curta || "-"}
                    </TableCell>
                    <TableCell>
                      {order.cliente?.razao ||
                        order.cliente?.fantasia ||
                        "-"}
                    </TableCell>
                    <TableCell>{order.objeto?.nome || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={order.status?.nome || "-"}
                        color={order.data_fechada ? "default" : "primary"}
                        variant={order.data_fechada ? "outlined" : "default"}
                      />
                    </TableCell>
                    <TableCell>{order.tipo?.nome || "-"}</TableCell>
                  </TableRow>
                ))}
              {loading && (
                <TableRow>
                  <TableCell colSpan={11} className={classes.emptyState}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} className={classes.emptyState}>
                    Nenhuma ordem de serviço encontrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[15, 25, 50, 100]}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count}`
          }
          onChangePage={(_, nextPage) => setPage(nextPage)}
          onChangeRowsPerPage={event => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
        />
      </Paper>

    </MainContainer>
  );
};

export default OrdensServico;
